/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useTranslation } from 'react-i18next'
import { useStatus } from '@/hooks/use-status'
import { formatBalanceWith, type CurrencyOption, type FormatOptions } from './format-quota'

export type { FormatOptions }

/**
 * Seed currencies used as a fallback before the backend supplies
 * `display_currencies` via `/api/status`.  Rates are expressed relative to
 * 500 000 quota-units = $1 USD (the backend default).
 */
const SEED: CurrencyOption[] = [
  {
    code: 'CNY',
    symbol: '¥',
    rate: 1.0,
    locales: ['zh'],
    enabled: true,
    is_default: true,
  },
  {
    code: 'USD',
    symbol: '$',
    rate: 0.5,
    locales: ['en', 'fr', 'ru', 'vi', 'ko'],
    enabled: true,
  },
  {
    code: 'JPY',
    symbol: '¥',
    rate: 80,
    locales: ['ja'],
    enabled: true,
  },
]

type FormatBalanceFn = (quota: number, options?: FormatOptions) => string

/**
 * An extended formatter returned by `useFormatBalance`.
 *
 * - Callable directly as `formatBalance(quota)` — backward-compatible with all
 *   existing callers.
 * - `.formatBalance(quota)` — same as calling directly.
 * - `.formatAmount(yuanAmount)` — treats the input as a "yuan" value (the
 *   canonical CNY internal unit) rather than raw quota units.  Equivalent to
 *   `formatBalance(yuanAmount × quotaPerUnit)`.  Used by the TopUp page where
 *   preset values are configured in yuan.
 */
export type FormatBalanceHook = FormatBalanceFn & {
  formatBalance: FormatBalanceFn
  formatAmount: (yuanAmount: number, options?: FormatOptions) => string
}

/**
 * Returns a formatter that converts raw quota units into a locale-aware
 * currency string.  The returned value is callable directly (backward compat)
 * and also exposes `.formatBalance` and `.formatAmount` convenience methods.
 *
 * Reads `display_currencies` and `quota_per_unit` from the global status
 * response so that admin-configured currencies are respected automatically.
 * Falls back to the SEED currencies until the status response arrives.
 *
 * @example
 * // existing callers — unchanged
 * const formatBalance = useFormatBalance()
 * return <span>{formatBalance(user.quota)}</span>
 *
 * @example
 * // new callers using yuan-denominated amounts (e.g. TopUp preset prices)
 * const formatBalance = useFormatBalance()
 * return <span>{formatBalance.formatAmount(displayValue)}</span>
 */
export function useFormatBalance(): FormatBalanceHook {
  const { status } = useStatus()
  const { i18n } = useTranslation()

  const currencies =
    (status?.display_currencies as CurrencyOption[] | undefined) ?? SEED
  const quotaPerUnit = (status?.quota_per_unit as number | undefined) ?? 500_000
  const locale = (i18n.language || 'zh').toLowerCase()

  const formatBalance: FormatBalanceFn = (quota, options) =>
    formatBalanceWith(quota, locale, currencies, quotaPerUnit, options)

  const formatAmount = (yuanAmount: number, options?: FormatOptions): string =>
    formatBalanceWith(yuanAmount * quotaPerUnit, locale, currencies, quotaPerUnit, options)

  // Cast to the extended type so the function is both callable and has named
  // sub-methods.  TypeScript allows assigning properties to a function object.
  const callable = formatBalance as FormatBalanceHook
  callable.formatBalance = formatBalance
  callable.formatAmount = formatAmount
  return callable
}
